/**
 * Wijzigingen testen — pure logica (filteren, groeperen per pagina, omgevingslabel, testpunten).
 * Los van de pagina zodat het zonder netwerk te testen is.
 */
export const TEST_URL = 'https://test.fullframe.nl';
export const LIVE_URL = 'https://intern.fullframe.nl';
export const ACHTER_DE_SCHERMEN = 'Achter de schermen';
export const RECENT_DAGEN = 14;

export const OMGEVING_TEKST = {
  live: 'Live',
  test_live_gepland: 'Test → live vanavond',
  test: 'Test',
};

export const STATUS_TEKST = { open: 'Nog testen', werkt: 'Werkt', probleem: 'Probleem' };

/**
 * Tabbladen en secties van Admin (pages/AdminSettings/AdminSettings.jsx), voor de koppen:
 * "/admin/settings?tab=mailboxen" → "Admin → Algemene mailboxen". Bewust een kopie (de
 * beheerpagina laden voor een paar namen is te zwaar); __tests__/Wijzigingen.test.jsx
 * controleert dat elke key uit AdminSettings.jsx hier met hetzelfde label staat.
 */
export const ADMIN_LABELS = {
  users: 'Gebruikersbeheer', app: 'Applicatiebeheer', logging: 'Logging', mailboxen: 'Algemene mailboxen',
  feedback: 'Feedback', 'teams-kanaal': 'Teams-kanaalsjabloon',
  roles: 'Gebruikers', onboarding: 'Medewerker onboarden', groepen: 'Groepen', 'apps-rollen': 'Apps & rollen', 'agenda-blokken': 'Taken in agenda',
  'satellite-wizard': 'Nieuwe tool (wizard)', 'satellite-approvals': 'Satelliet-aanvragen',
  'satellite-usage': 'Satelliet-gebruik', wijzigingslog: 'Wijzigingslog', 'klant-afkortingen': 'Klant-afkortingen',
  'very-video-bureaus': 'Very Video-bureaus', portfolios: "Portfolio's", 'inplannen-producten': 'Nog in te plannen',
  'planning-regels': 'Planning-regels', 'slim-uren-regels': 'Slim Uren-regels', taakgeneratie: 'Taakgeneratie',
  scriptstijlen: 'Scriptstijlen', taaksjablonen: 'Taaksjablonen', 'achmea-tarieven': 'Achmea tarieven', klantvereisten: 'Klantvereisten',
  'mail-toegang': 'Mail & agenda-toegang', 'integratie-status': 'Integratie-status',
  'sharepoint-storage': 'SharePoint opslag', shortcuts: 'Dashboard shortcuts',
  'dashboard-templates': 'Dashboard templates', 'ai-models': 'AI modellen', 'ai-tegoed': 'AI-tegoed',
  'ai-prompts': 'AI prompts', 'ai-monitoring': 'AI monitoring', 'wiki-kennis': 'Wiki-kennis',
  'script-breakdown': 'Script breakdown', 'render-servers': 'Render servers', 'corvee-tasks': 'Corvee taken',
  'opdracht-resync': 'Opdracht re-sync', paginagebruik: 'Paginagebruik', 'render-logs': 'Render-server jobs',
  'render-updates': 'Render updates', 'recorder-logs': 'Recorder logs', 'sales-mail-logs': 'Sales-mail logs',
  'freelancer-mail': 'Freelancer-mailadressen', info: 'info@', ikwilwerken: 'ikwilwerken@', log: 'Log',
};

/**
 * Paginanaam voor een kop: uit de navigatie (labelVan), anders uit de zoeklijst (tools:
 * [{title, link}]), anders het pad zelf. Admin krijgt "Admin → <tabblad> → <sectie>".
 */
export function paginaLabel(route, labelVan = () => null, tools = []) {
  if (!route) return null;
  const [pad, query = ''] = route.split('?');
  if (pad === '/admin/settings' || pad === '/admin') {
    const params = new URLSearchParams(query);
    const delen = [params.get('tab'), params.get('section')].map((k) => (k ? ADMIN_LABELS[k] : null)).filter(Boolean);
    return ['Admin', ...delen].join(' → ');
  }
  const uitNav = labelVan(pad);
  if (uitNav) return uitNav;
  const tool = (tools || []).find((t) => t.link === pad);
  return tool ? tool.title : pad;
}

/** Vandaag/gisteren/"7 okt" — kort en zonder tijd. */
export function datumTekst(iso, nu = new Date()) {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  const dag = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const verschil = Math.round((dag(nu) - dag(d)) / 86400000);
  if (verschil === 0) return 'vandaag';
  if (verschil === 1) return 'gisteren';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function alsDatum(iso) {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Versie + datum die bij de omgeving van de wijziging horen. */
export function versieEnDatum(w) {
  const live = w.omgeving === 'live';
  const versie = (live ? w.versie_live : w.versie_test) || w.versie_test || w.versie_live || null;
  const datum = (live ? w.gemerged_live_at : w.gemerged_test_at) || w.gemerged_test_at || w.gemerged_live_at || null;
  return { versie, datum };
}

export function testLink(route) { return route ? `${TEST_URL}${route}` : null; }
export function liveLink(route) { return route ? `${LIVE_URL}${route}` : null; }

/** Eén Open-knop: live als het daar staat, anders test. */
export function openLink(route, omgeving) {
  return omgeving === 'live' ? liveLink(route) : testLink(route);
}

/** Niets om te testen: de server zegt het, of (oude rij) er is geen pagina. */
export function isAchter(w) {
  if (typeof w.achter_de_schermen === 'boolean') return w.achter_de_schermen;
  return !(w.routes && w.routes.length) && !(w.testpunten && w.testpunten.length);
}

/** Telt mee in "nog te testen": niet achter de schermen en niet als niet-relevant gemarkeerd. */
export function teltMee(w) { return !isAchter(w) && !w.niet_relevant; }

/** Nog iets voor mij: open, of een ✗ die nog niet is opgelost (blijft zichtbaar met de feedback). */
export function teTesten(w) { return teltMee(w) && (w.mijn_status || 'open') !== 'werkt'; }

/** Alles ✓ (bij testpunten: elk punt). */
export function isGetest(w) { return (w.mijn_status || 'open') === 'werkt'; }

/** Duur van het wegschuiven na ✓ (ms). */
export const VERDWIJN_MS = 350;

/** Testpunten die zichtbaar zijn: ✓-punten klappen in, tenzij "Toon getest" of ze net wegschuiven. */
export function zichtbarePunten(w, { toonGetest = false, animerend = new Set() } = {}) {
  return (w.testpunten || []).filter((p) => toonGetest || (p.mijn_status || 'open') !== 'werkt'
    || animerend.has(`p-${p.id}`));
}

export function klaarVoorLive(lijst) { return (lijst || []).filter((w) => w.live_goedgekeurd && !w.niet_relevant); }

/** Recent = gemerged of getest in de laatste 14 dagen. */
export function isRecent(w, nu = new Date()) {
  const grens = nu.getTime() - RECENT_DAGEN * 86400000;
  const momenten = [w.gemerged_test_at, w.gemerged_live_at, w.laatste_activiteit].map(alsDatum).filter(Boolean);
  return momenten.some((d) => d.getTime() >= grens);
}

/**
 * Waar de wijziging staat: per testpunt (een live wijziging kan punten hebben die alleen op
 * test staan, Codex r2), anders de wijziging zelf.
 */
export function omgevingen(w) {
  const punten = w.testpunten || [];
  return punten.length ? [...new Set(punten.map((p) => p.omgeving || w.omgeving))] : [w.omgeving];
}

/** Nog iets op test: de wijziging, of een testpunt dat (nog) alleen op test staat. */
export function staatOpTest(w) { return omgevingen(w).some((o) => o !== 'live'); }

/** "Ouder": niet recent en nog (deels) op test — ingeklapt onderaan. */
export function isOuder(w, nu = new Date()) { return staatOpTest(w) && !isRecent(w, nu); }

export function voortgangTekst(v) { return v ? `${v.getest} van ${v.totaal} getest` : null; }

/**
 * filters: { omgeving: 'alles'|'test'|'live', alleenTeTesten: bool, toonGetest: bool,
 *            klaarVoorLive: bool, pagina: 'alle'|<sleutel> }
 * Geteste wijzigingen (alles ✓) zijn standaard weg, ook met "nog te testen" uit; "Toon getest"
 * brengt ze terug. "Klaar voor live" toont alleen wat Joris heeft goedgekeurd (ongeacht status).
 * vastgehouden: ids die in deze sessie veranderd zijn of net wegschuiven — die blijven staan
 * tot de animatie klaar is of tot een verversing, anders springt de lijst onder je muis weg.
 */
export function filterWijzigingen(lijst, filters, vastgehouden = new Set()) {
  const {
    omgeving = 'alles', alleenTeTesten = false, toonGetest = false, klaarVoorLive: alleenKlaar = false, pagina = 'alle',
  } = filters || {};
  return (lijst || []).filter((w) => {
    if (omgeving === 'live' && !omgevingen(w).includes('live')) return false;
    if (omgeving === 'test' && !staatOpTest(w)) return false;
    if (pagina !== 'alle' && !paginaSleutels(w).includes(pagina)) return false;
    if (alleenKlaar) return !!w.live_goedgekeurd && !w.niet_relevant;
    if (vastgehouden.has(w.id)) return true;
    if (!toonGetest && isGetest(w)) return false;
    if (alleenTeTesten && !teTesten(w)) return false;
    return true;
  });
}

/**
 * Splits in de hoofdlijst en "Ouder" (ingeklapt). Met `laatste14` alleen recente in de
 * hoofdlijst; oudere die nog op test staan gaan naar "Ouder", oudere live verdwijnen.
 * Zonder `laatste14` staat alles in de hoofdlijst.
 */
export function splitsOuder(lijst, { laatste14 = true } = {}, nu = new Date(), vastgehouden = new Set()) {
  if (!laatste14) return { hoofd: lijst || [], ouder: [] };
  const hoofd = [];
  const ouder = [];
  for (const w of lijst || []) {
    if (isRecent(w, nu) || vastgehouden.has(w.id)) hoofd.push(w);
    else if (isOuder(w, nu)) ouder.push(w);
  }
  return { hoofd, ouder };
}

/** Alle pagina's van een wijziging; zonder route → "Achter de schermen". */
export function paginaSleutels(w) {
  if (isAchter(w)) return [ACHTER_DE_SCHERMEN];
  if (w.routes && w.routes.length) return w.routes;
  const uitPunten = [...new Set((w.testpunten || []).map((p) => p.route).filter(Boolean))];
  return uitPunten.length ? uitPunten : [ACHTER_DE_SCHERMEN];
}

/**
 * Groepen per pagina, in de volgorde van de nieuwste wijziging erin (de lijst komt al
 * nieuwste-eerst van de server). Een wijziging staat één keer, onder zijn eerste pagina —
 * behalve als er op een andere pagina van die wijziging gefilterd wordt: dan staat hij daar
 * (rustiger dan dezelfde regel onder elke pagina herhalen). "Achter de schermen" onderaan.
 * labelVan(route) → paginanaam of null.
 */
export function groepeerPerPagina(lijst, labelVan = () => null, pagina = 'alle') {
  const groepen = new Map();
  for (const w of lijst || []) {
    const sleutels = paginaSleutels(w);
    const sleutel = sleutels.includes(pagina) ? pagina : sleutels[0];
    if (!groepen.has(sleutel)) {
      const isAchterGroep = sleutel === ACHTER_DE_SCHERMEN;
      groepen.set(sleutel, {
        sleutel,
        route: isAchterGroep ? null : sleutel,
        label: isAchterGroep ? ACHTER_DE_SCHERMEN : (labelVan(sleutel) || sleutel),
        items: [],
      });
    }
    groepen.get(sleutel).items.push(w);
  }
  const uit = [...groepen.values()];
  return [...uit.filter((g) => g.route), ...uit.filter((g) => !g.route)];
}

/** Paginakeuzes voor het filter: alle pagina's die in de lijst voorkomen, op naam. */
export function paginaOpties(lijst, labelVan = () => null) {
  const sleutels = [...new Set((lijst || []).flatMap(paginaSleutels))];
  return sleutels
    .map((sleutel) => ({ sleutel, label: sleutel === ACHTER_DE_SCHERMEN ? sleutel : (labelVan(sleutel) || sleutel) }))
    .sort((a, b) => (a.sleutel === ACHTER_DE_SCHERMEN) - (b.sleutel === ACHTER_DE_SCHERMEN)
      || a.label.localeCompare(b.label, 'nl'));
}

/**
 * Eén testpunt lokaal bijwerken en de wijziging opnieuw samenvatten (zelfde regels als de
 * server): een open punt → wijziging open; anders een probleem → probleem; anders werkt.
 */
export function werkPuntBij(w, testpuntId, wijzig) {
  const testpunten = (w.testpunten || []).map((p) => (p.id === testpuntId ? { ...p, ...wijzig } : p));
  if (!testpunten.length) return w;
  const statussen = testpunten.map((p) => p.mijn_status || 'open');
  let mijnStatus = 'werkt';
  if (statussen.includes('open')) mijnStatus = 'open';
  else if (statussen.includes('probleem')) mijnStatus = 'probleem';
  return {
    ...w,
    testpunten,
    mijn_status: mijnStatus,
    voortgang: { getest: statussen.filter((s) => s !== 'open').length, totaal: statussen.length },
  };
}
